import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
    Container,
    Paper,
    Grid,
    Typography,
    Button,
    Card,
    CardContent,
    CardHeader,
    Divider,
    Chip,
    Tooltip,
    CircularProgress,
    Box,
    Breadcrumbs,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Snackbar,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { Alert } from "@mui/material";
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import PictureAsPdfIcon from '@material-ui/icons/PictureAsPdf';
import DescriptionIcon from '@material-ui/icons/Description';
import CancelIcon from '@material-ui/icons/Cancel';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import WarningIcon from '@material-ui/icons/Warning';
import PersonIcon from '@material-ui/icons/Person';
import EmailIcon from '@material-ui/icons/Email';
import PhoneIcon from '@material-ui/icons/Phone';
import AssignmentIcon from '@material-ui/icons/Assignment';
import UserService from "../../services/UserService";
import ContractService from "../../services/ContractService";
import MainContainer from "../../components/MainContainer";
import { headerContract } from "../../data/modules/contract_header";
import CreateAttestationDialog from "./CreateAttestationDialog";

const useStyles = makeStyles((theme) => ({
    container: {
        padding: theme.spacing(3),
        marginTop: theme.spacing(2),
    },
    header: {
        marginBottom: theme.spacing(3),
        padding: theme.spacing(2),
        backgroundColor: '#1976d2',
        borderRadius: '8px',
        color: 'white',
    },
    backButton: {
        marginBottom: theme.spacing(2),
    },
    sectionCard: {
        marginBottom: theme.spacing(3),
        borderRadius: '12px',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
    },
    infoRow: {
        marginBottom: theme.spacing(2),
        padding: theme.spacing(1),
        borderBottom: '1px solid #e0e0e0',
    },
    infoLabel: {
        fontWeight: 'bold',
        color: theme.palette.text.secondary,
        marginBottom: theme.spacing(0.5),
    },
    infoValue: {
        fontSize: '1.1rem',
    },
    documentCard: {
        textAlign: 'center',
        padding: theme.spacing(2),
        cursor: 'pointer',
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: '0 6px 16px rgba(0, 0, 0, 0.12)',
        },
    },
    documentIcon: {
        fontSize: 60,
        marginBottom: theme.spacing(1),
    },
    documentTitle: {
        fontWeight: 'bold',
        marginBottom: theme.spacing(1),
    },
    statusBadge: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: theme.spacing(0.5),
        padding: theme.spacing(0.5, 1.5),
        borderRadius: '20px',
        fontSize: '0.875rem',
        fontWeight: 'bold',
    },
    statusComplete: {
        backgroundColor: '#4caf50',
        color: 'white',
    },
    statusMissing: {
        backgroundColor: '#f44336',
        color: 'white',
    },
    statusPartial: {
        backgroundColor: '#ff9800',
        color: 'white',
    },
    statusActive: {
        backgroundColor: '#4caf50',
        color: 'white',
    },
    statusExpired: {
        backgroundColor: '#f44336',
        color: 'white',
    },
    statusChip: {
        fontWeight: 'bold',
    },
    loadingContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '400px',
    },
    attestationSection: {
        marginTop: theme.spacing(3),
        textAlign: 'center',
        padding: theme.spacing(3),
    },
    tableContainer: {
        marginTop: theme.spacing(2),
        overflowX: 'auto',
    },
    tableHeader: {
        backgroundColor: theme.palette.grey[200],
    },
    tableRow: {
        '&:hover': {
            backgroundColor: theme.palette.action.hover,
        },
    },
}));

export default function StagiaireDetails() {
    const classes = useStyles();
    const { id } = useParams();
    const navigate = useNavigate();
    
    const [stagiaire, setStagiaire] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [detailaffectation, setDetailsaffectation] = useState([]);
    const [fileData, setFileData] = useState(null);
    
    // États pour l'affichage du document
    const [documentDialogOpen, setDocumentDialogOpen] = useState(false);
    
    // États pour la vérification d'accès
    const [verifying, setVerifying] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState('info');
    
    // États pour la création d'attestation
    const [createAttestationOpen, setCreateAttestationOpen] = useState(false);

    useEffect(() => {
        loadStagiaireDetails();
        getDetailsaffectaion();
    }, [id]);

    const getContractStatus = (dateFin) => {
        if (!dateFin) return { label: 'En cours', color: 'statusActive' };
        const endDate = new Date(dateFin);
        const today = new Date();
        if (endDate < today) {
            return { label: 'Expiré', color: 'statusExpired' };
        }
        return { label: 'Actif', color: 'statusActive' };
    };

    const loadStagiaireDetails = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await UserService.contractstg();
            if (response && response.status) {
                const data = response.data || response;
                const found = data.find(s => s.user_id === id);
                
                if (found) {
                    setStagiaire(found);
                } else {
                    setError("Stagiaire non trouvé");
                }
            } else {
                setError("Erreur lors du chargement des données");
            }
        } catch (err) {
            console.error("Erreur:", err);
            setError("Impossible de charger les détails du stagiaire");
        } finally {
            setLoading(false);
        }
    };
    
    const getDetailsaffectaion = async () => {
        const response = await ContractService.getdetailsaffectation(id);
        if (response && response.data) {
            setDetailsaffectation(response.data);
        }
    };

    const handleDownload = () => {
        if (fileData && stagiaire) {
             ContractService.actionacces(stagiaire.user_id, fileData.document_type,"download").then((result) => {
                if (result && result.status) {
                }
            }).catch((err) => {

            });
            const link = document.createElement('a');
            link.href = `/contract/download_document?stagiaire_id=${stagiaire.user_id}&document_type=${fileData.document_type}`;
            link.download = fileData.file_name;
            link.click();
        }
    };

    const handlePrint = () => {
        if (fileData) {
             ContractService.actionacces(stagiaire.user_id, fileData.document_type,"print").then((result) => {
                if (result && result.status) {
                }
            }).catch((err) => {

            });
            const printWindow = window.open('', '_blank');
            printWindow.document.write(`
                <html>
                    <head>
                        <title>${fileData.file_name}</title>
                        <style>
                            body { margin: 0; padding: 20px; }
                            embed { width: 100%; height: 100vh; }
                        </style>
                    </head>
                    <body>
                        <embed src="data:${fileData.file_type};base64,${fileData.file_content}" width="100%" height="100%" />
                        <script>
                            window.onload = function() {
                                setTimeout(() => {
                                    window.print();
                                }, 1000);
                            }
                        </script>
                    </body>
                </html>
            `);
            printWindow.document.close();
        }
    };

    const handleVerify = (stagiaireId, documentType, action) => {
        setVerifying(true);
        
        ContractService.actionacces(stagiaireId, documentType, action).then((result) => {
            if (result && result.status) {
                setFileData({ ...result.data, document_type: documentType });
                setDocumentDialogOpen(true);
            }
            setVerifying(false);
        }).catch((err) => {
            setFileData(null);
            setVerifying(false);
            showSnackbar("Impossible d'accéder au document", 'error');
        });
    };

    const showSnackbar = (message, severity) => {
        setSnackbarMessage(message);
        setSnackbarSeverity(severity);
        setSnackbarOpen(true);
    };

    const handleCloseSnackbar = () => {
        setSnackbarOpen(false);
    };

    const handleViewDocument = (documentType, documentUrl, documentTitle) => {
        if (!documentUrl) {
            showSnackbar(`Le document "${documentTitle}" n'est pas disponible.`, 'error');
            return;
        }
        
        handleVerify(stagiaire?.user_id, documentType, 'view');
    };

    const handleCloseDocumentDialog = () => {
        setDocumentDialogOpen(false);
        setTimeout(() => {
            setFileData(null);
        }, 200);
    };

    const handleCreateAttestation = () => {
        setCreateAttestationOpen(true);
    };

    const handleAttestationSuccess = () => {
        loadStagiaireDetails(); 
        setCreateAttestationOpen(false);
    };

    const getStagiaireStatus = () => {
        if (!stagiaire) return { label: 'Inconnu', color: 'statusMissing', icon: <WarningIcon /> };
        
        const hasContrat = stagiaire.has_contrat || stagiaire.file_contrat;
        const hasAssurance = stagiaire.has_assurance || stagiaire.file_attestation;
        const hasDiplome = stagiaire.has_diplome || stagiaire.file_diplome;
        
        if (hasContrat && hasAssurance && hasDiplome) {
            return { label: 'Dossier Complet', color: 'statusComplete', icon: <CheckCircleIcon /> };
        } else if (!hasContrat && !hasAssurance && !hasDiplome) {
            return { label: 'Dossier Manquant', color: 'statusMissing', icon: <CancelIcon /> };
        } else {
            return { label: 'Dossier Partiel', color: 'statusPartial', icon: <WarningIcon /> };
        }
    };

    if (loading) {
        return (
            <MainContainer {...headerContract}>
                <div className={classes.loadingContainer}>
                    <CircularProgress size={60} />
                </div>
            </MainContainer>
        );
    }

    if (error || !stagiaire) {
        return (
            <MainContainer {...headerContract}>
                <Container className={classes.container}>
                    <Alert severity="error" style={{ marginBottom: '20px' }}>
                        {error || "Stagiaire non trouvé"}
                    </Alert>
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<ArrowBackIcon />}
                        onClick={() => navigate('/contract_stg')}
                    >
                        Retour à la liste
                    </Button>
                </Container>
            </MainContainer>
        );
    }

    const status = getStagiaireStatus();

    return (
        <MainContainer {...headerContract}>
            <Container maxWidth="lg" className={classes.container}>
                {/* En-tête avec navigation */}
                <Paper className={classes.header}>
                    <Breadcrumbs style={{ color: 'white', marginBottom: '16px' }}>
                        <Typography color="textPrimary">Détails du stagiaire</Typography>
                    </Breadcrumbs>
                    
                    <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap">
                        <Box display="flex" alignItems="center" gap={2}>
                            <PersonIcon style={{ fontSize: 40 }} />
                            <div>
                                <Typography variant="h4">
                                    {stagiaire.full_name?.toUpperCase() || `${stagiaire.user_last_name?.toUpperCase() || ''} ${stagiaire.user_first_name || ''}`.trim()}
                                </Typography>
                                <Typography variant="subtitle1">
                                    Matricule: {stagiaire.registration_number || 'Non renseigné'}
                                </Typography>
                            </div>
                        </Box>
                        <div className={`${classes.statusBadge} ${classes[status.color]}`}>
                            {status.icon} {status.label}
                        </div>
                    </Box>
                </Paper>

                <Button
                    className={classes.backButton}
                    variant="outlined"
                    color="primary"
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate('/contract_stg')}
                >
                    Retour à la liste des stagiaires
                </Button>

                <Grid container spacing={3}>
                    {/* Informations personnelles */}
                    <Grid item xs={12} md={6}>
                        <Card className={classes.sectionCard}>
                            <CardHeader 
                                title="Informations personnelles"
                                avatar={<PersonIcon color="primary" />}
                            />
                            <Divider />
                            <CardContent>
                                <div className={classes.infoRow}>
                                    <Typography className={classes.infoLabel}>CIN / Matricule</Typography>
                                    <Typography className={classes.infoValue}>
                                        {stagiaire.cin || stagiaire.registration_number || 'Non renseigné'}
                                    </Typography>
                                </div>
                                <div className={classes.infoRow}>
                                    <Typography className={classes.infoLabel}>
                                        <EmailIcon style={{ fontSize: 16, verticalAlign: 'middle', marginRight: 4 }} />
                                        Email
                                    </Typography>
                                    <Typography className={classes.infoValue}>
                                        {stagiaire.user_email || stagiaire.email || 'Non renseigné'}
                                    </Typography>
                                </div>
                                <div className={classes.infoRow}>
                                    <Typography className={classes.infoLabel}>
                                        <PhoneIcon style={{ fontSize: 16, verticalAlign: 'middle', marginRight: 4 }} />
                                        Téléphone Orange
                                    </Typography>
                                    <Typography className={classes.infoValue}>
                                        {stagiaire.phone_orange || 'Non renseigné'}
                                    </Typography>
                                </div>
                                <div className={classes.infoRow}>
                                    <Typography className={classes.infoLabel}>
                                        <PhoneIcon style={{ fontSize: 16, verticalAlign: 'middle', marginRight: 4 }} />
                                        Téléphone Parental
                                    </Typography>
                                    <Typography className={classes.infoValue}>
                                        {stagiaire.phone_parent || 'Non renseigné'}
                                    </Typography>
                                </div>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Statistiques et statut */}
                    <Grid item xs={12} md={6}>
                        <Card className={classes.sectionCard}>
                            <CardHeader 
                                title="Statut du dossier"
                                avatar={<AssignmentIcon color="primary" />}
                            />
                            <Divider />
                            <CardContent>
                                <div className={classes.infoRow}>
                                    <Typography className={classes.infoLabel}>Statut global</Typography>
                                    <div className={`${classes.statusBadge} ${classes[status.color]}`}>
                                        {status.icon} {status.label}
                                    </div>
                                </div>
                                <div className={classes.infoRow}>
                                    <Typography className={classes.infoLabel}>Date d'inscription</Typography>
                                    <Typography className={classes.infoValue}>
                                        {stagiaire.creatdate ? new Date(stagiaire.creatdate).toLocaleDateString('fr-FR') : 'Non renseignée'}
                                    </Typography>
                                </div>
                            </CardContent>
                        </Card>
                    </Grid>
                    
                    {/* Historiques */}
                    <Grid item xs={12}>
                        <Card className={classes.sectionCard}>
                            <CardHeader 
                                title="Contrat"
                                avatar={<DescriptionIcon color="primary" />}
                            />
                            <Divider />
                            <CardContent>
                                <Grid container spacing={3}>
                                    <Grid item xs={12} sm={12} md={12}>
                                        <TableContainer component={Paper} className={classes.tableContainer}>
                                            <Table size="small">
                                                <TableHead className={classes.tableHeader}>
                                                    <TableRow>
                                                        <TableCell>N°</TableCell>
                                                        <TableCell>Direction</TableCell>
                                                        <TableCell>Service</TableCell>
                                                        <TableCell>Fonction</TableCell>
                                                        <TableCell>Financement</TableCell>
                                                        <TableCell>Date début contrat</TableCell>
                                                        <TableCell>Date fin contrat</TableCell>
                                                        <TableCell>Statut</TableCell>
                                                        <TableCell>Contrat</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {detailaffectation && detailaffectation.length > 0 ? (
                                                        detailaffectation.map((element, index) => {
                                                            const contractStatus = getContractStatus(element.date_fin);
                                                            
                                                            return (
                                                                <TableRow key={element.user_affectation_id || index} className={classes.tableRow}>
                                                                    <TableCell>
                                                                        <Chip 
                                                                            label={index+1}
                                                                            size="small"
                                                                            variant="outlined"
                                                                        />
                                                                    </TableCell>
                                                                    <TableCell>{element.direction_name || '-'}</TableCell>
                                                                    <TableCell>{element.service_name || '-'}</TableCell>
                                                                    <TableCell>{element.function_name || '-'}</TableCell>
                                                                    <TableCell>{element.funding_code || '-'}</TableCell>
                                                                    <TableCell>{element.date_prise || '-'}</TableCell>
                                                                    <TableCell>{element.date_fin || '-'}</TableCell>
                                                                    <TableCell>
                                                                        <Chip 
                                                                            label={contractStatus.label}
                                                                            size="small"
                                                                            className={`${classes.statusChip} ${classes[contractStatus.color]}`}
                                                                        />
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        {element.file_contract ? (
                                                                            <Tooltip title="Cliquez pour visualiser le document">
                                                                                <Button 
                                                                                    size="small" 
                                                                                    color="primary" 
                                                                                    startIcon={<PictureAsPdfIcon />}
                                                                                    onClick={() => handleViewDocument('contrat', element.file_contract, `Contrat ${element.function_name || ''}`)}
                                                                                >
                                                                                    Voir le document
                                                                                </Button>
                                                                            </Tooltip>
                                                                        ) : (
                                                                            <Tooltip title="Contrat">
                                                                                <Typography variant="body2" color="textSecondary">
                                                                                    Aucun contrat
                                                                                </Typography>
                                                                            </Tooltip>
                                                                        )}
                                                                    </TableCell>
                                                                </TableRow>
                                                            );
                                                        })
                                                    ) : (
                                                        <TableRow>
                                                            <TableCell colSpan={9} align="center">
                                                                <Typography variant="body2" color="textSecondary">
                                                                    Aucune affectation trouvée
                                                                </Typography>
                                                            </TableCell>
                                                        </TableRow>
                                                    )}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    </Grid>
                    
                    {/* Documents */}
                    <Grid item xs={12}>
                        <Card className={classes.sectionCard}>
                            <CardHeader 
                                title="Documents"
                                avatar={<DescriptionIcon color="primary" />}
                            />
                            <Divider />
                            <CardContent>
                                <Grid container spacing={3}>
                                    <Grid item xs={12} sm={6} md={6}>
                                        <Paper 
                                            className={classes.documentCard}
                                            onClick={() => handleViewDocument('assurance', stagiaire.file_attestation, "Assurance")}
                                            style={{ cursor: stagiaire.file_attestation ? 'pointer' : 'default' }}
                                        >
                                            <DescriptionIcon 
                                                className={classes.documentIcon}
                                                style={{ color: stagiaire.file_attestation ? '#4caf50' : '#f44336' }}
                                            />
                                            <Typography className={classes.documentTitle}>Assurance</Typography>
                                            {stagiaire.file_attestation ? (
                                                <Tooltip title="Cliquez pour visualiser le document">
                                                    <Button size="small" color="primary" startIcon={<PictureAsPdfIcon />}>
                                                        Voir le document
                                                    </Button>
                                                </Tooltip>
                                            ) : (
                                                <Chip label="Document manquant" size="small" color="secondary" />
                                            )}
                                        </Paper>
                                    </Grid>

                                    <Grid item xs={12} sm={6} md={6}>
                                        <Paper 
                                            className={classes.documentCard}
                                            onClick={() => handleViewDocument('diplome', stagiaire.file_diplome, "Diplôme")}
                                            style={{ cursor: stagiaire.file_diplome ? 'pointer' : 'default' }}
                                        >
                                            <DescriptionIcon 
                                                className={classes.documentIcon}
                                                style={{ color: stagiaire.file_diplome ? '#4caf50' : '#f44336' }}
                                            />
                                            <Typography className={classes.documentTitle}>Diplôme</Typography>
                                            {stagiaire.file_diplome ? (
                                                <Tooltip title="Cliquez pour visualiser le document">
                                                    <Button size="small" color="primary" startIcon={<PictureAsPdfIcon />}>
                                                        Voir le document
                                                    </Button>
                                                </Tooltip>
                                            ) : (
                                                <Chip label="Document manquant" size="small" color="secondary" />
                                            )}
                                        </Paper>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Section création attestation */}
                    {!stagiaire.file_attesation_stage && (
                        <Grid item xs={12}>
                            <Card className={classes.sectionCard}>
                                <CardHeader 
                                    title="Attestation de stage"
                                    avatar={<PictureAsPdfIcon color="secondary" />}
                                />
                                <Divider />
                                <CardContent className={classes.attestationSection}>
                                    <Typography variant="body1" color="textSecondary" gutterBottom>
                                        Vous n'avez pas encore d'attestation pour ce stagiaire.
                                    </Typography>
                                    <Button
                                        variant="contained"
                                        color="secondary"
                                        size="large"
                                        onClick={handleCreateAttestation}
                                        startIcon={<PictureAsPdfIcon />}
                                        style={{ marginTop: '16px' }}
                                    >
                                        Créer une attestation de stage
                                    </Button>
                                </CardContent>
                            </Card>
                        </Grid>
                    )}
                    {stagiaire.file_attesation_stage && (
                        <Grid item xs={12}>
                            <Card className={classes.sectionCard}>
                                <CardHeader 
                                    title="Attestation de stage"
                                    avatar={<PictureAsPdfIcon color="secondary" />}
                                />
                                <Divider />
                                <CardContent className={classes.attestationSection}>
                                    <Paper 
                                        className={classes.documentCard}
                                        onClick={() => handleViewDocument('attestation', stagiaire.file_attesation_stage, "Attestation de stage")}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        <DescriptionIcon 
                                            className={classes.documentIcon}
                                            style={{ color: '#4caf50' }}
                                        />
                                        <Typography className={classes.documentTitle}>Attestation de stage</Typography>
                                        <Tooltip title="Cliquez pour visualiser le document">
                                            <Button size="small" color="primary" startIcon={<PictureAsPdfIcon />}>
                                                Voir le document
                                            </Button>
                                        </Tooltip>
                                    </Paper>    
                                </CardContent>
                            </Card>
                        </Grid>
                    )}
                </Grid>

                {/* Dialogue de visualisation du document */}
                <Dialog open={documentDialogOpen} onClose={handleCloseDocumentDialog} maxWidth="xl" fullWidth>
                    <DialogTitle>
                        Visualisation du document
                        <Typography variant="subtitle2" color="textSecondary">
                            {fileData?.file_name}
                        </Typography>
                    </DialogTitle>
                    <DialogContent>
                        <Box height="70vh">
                            {fileData && (
                                <embed
                                    src={`data:${fileData.file_type};base64,${fileData.file_content}`}
                                    type={fileData.file_type}
                                    width="100%"
                                    height="100%"
                                />
                            )}
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handlePrint} color="primary">
                            🖨️ Imprimer
                        </Button>
                        <Button onClick={handleDownload} color="primary">
                            💾 Télécharger
                        </Button>
                        <Button onClick={handleCloseDocumentDialog} color="secondary">
                            Fermer
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Dialogue de création d'attestation */}
                <CreateAttestationDialog
                    open={createAttestationOpen}
                    onClose={() => setCreateAttestationOpen(false)}
                    stagiaireId={stagiaire?.user_id}
                    onSuccess={handleAttestationSuccess}
                    initialData={{
                        nomPrenom: stagiaire?.full_name || `${stagiaire?.user_last_name || ''} ${stagiaire?.user_first_name || ''}`.trim(),
                        dateDebut: "2026-01-01",
                        dateFin: "2026-02-28",
                        service: "Management Qualité",
                        direction: "Direction Principale chargée de la Coordination, Administration, Finances et Système de paiement",
                        directeurNom: "RAZAKATOANINA Achille",
                        organisme: "FONDS D'INTERVENTION POUR LE DEVELOPPEMENT (FID)",
                        lieu: "Antananarivo"
                    }}
                />

                {/* Snackbar pour les notifications */}
                <Snackbar
                    open={snackbarOpen}
                    autoHideDuration={6000}
                    onClose={handleCloseSnackbar}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                >
                    <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity}>
                        {snackbarMessage}
                    </Alert>
                </Snackbar>
            </Container>
        </MainContainer>
    );
}